import React, { memo } from 'react';

import { Button, IButtonProps } from '~/UIKit';
import ModalComponent from '~/Components/Modal/Modal.component';
import {
  PRIMARY_BLUE,
  FITOGRAM_BLUE_DARK_2,
  GRAY_BACKGROUND_2,
  GRAY_DISABLED,
  NOTE_RED,
  RED_USC,
  WHITE,
} from '~/Constants/Colors.constants';
import { useLanguage } from '~/Hooks';
import { makeStyles } from '~/UIKit/Style';
import {
  FONT_SIZE_LARGISH,
  FONT_SIZE_MEDIUM_INBETWEEN,
  FONT_SIZE_XX_LARGER,
} from '~/Constants/UI.constants';
import { CloseIcon } from '../Icons/CloseIcon';
import { useMediaQuery } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { BOX_SHADOW_TOP_V2 } from '~/Constants/CSS.constants';
import cn from 'classnames';

interface ButtonProps extends IButtonProps {
  title?: string;
  action?: () => void;
  disabled?: boolean;
}

export interface ConfirmationModalProps {
  open: boolean;
  title: string;
  type?: 'horizontal' | 'vertical';
  children?: React.ReactNode;
  confirm: ButtonProps;
  cancel: ButtonProps;
  destructive?: boolean;
  closeAction?: () => void;
  rawContent?: boolean;
  hideCloseIcon?: boolean;
}

const useStyles = (destructive: boolean) =>
  makeStyles(
    (theme) => ({
      topSection: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '30px 16px 16px',
        borderBottom: `1px solid ${GRAY_BACKGROUND_2}`,
        '& p': {
          fontSize: FONT_SIZE_LARGISH,
          lineHeight: '28px',
          fontWeight: 'bold',
          letterSpacing: '-0.4px',
          margin: 0,
          [theme.breakpoints.up('md')]: {
            fontSize: FONT_SIZE_XX_LARGER,
            lineHeight: '42px',
            letterSpacing: '-0.75px',
          },
        },
      },
      content: {
        padding: '24px 16px 48px',
        overflowY: 'auto',
        [theme.breakpoints.up('md')]: {
          padding: '24px 0 58px',
        },
      },
      buttonWrapper: {
        display: 'flex',
        flexDirection: 'initial',
        justifyContent: 'space-around',
        alignItems: 'center',
        gap: 24,
        padding: '16px',
        borderTop: `1px solid ${GRAY_BACKGROUND_2}`,
        boxShadow: BOX_SHADOW_TOP_V2,
        [theme.breakpoints.up('md')]: {
          columnGap: 42,
          padding: 0,
          boxShadow: 'none',
          borderTop: 'none',
        },
      },
      buttonWrapperRawContent: {
        display: 'flex',
        flexDirection: 'initial',
        justifyContent: 'space-around',
        alignItems: 'center',
        gap: 24,
        padding: '16px',
        borderTop: `1px solid ${GRAY_BACKGROUND_2}`,
        boxShadow: BOX_SHADOW_TOP_V2,
      },
      button: {
        textTransform: 'capitalize',
        fontSize: FONT_SIZE_MEDIUM_INBETWEEN,
        width: '100%',
        borderRadius: 4,
        [theme.breakpoints.up('md')]: {
          width: 255,
        },
        '&.MuiButton-containedError': {
          background: `${destructive ? RED_USC : PRIMARY_BLUE}`,
          '&:hover': {
            background: `${destructive ? NOTE_RED : FITOGRAM_BLUE_DARK_2}`,
          },
          '&:disabled': {
            background: GRAY_DISABLED,
            color: WHITE,
            cursor: 'not-allowed',
            pointerEvents: 'none',
          },
        },
      },
      buttonsVerticalPosition: {
        flexDirection: 'column',
        placeItems: 'center',
      },
      cancel: {
        cursor: 'pointer',
      },
    }),
    { name: 'ConfirmationModal' },
  );

export const ConfirmationModal = memo(
  ({
    open,
    type = 'vertical',
    confirm,
    cancel,
    children,
    title,
    destructive = false,
    closeAction,
    rawContent = false,
    hideCloseIcon = false,
  }: ConfirmationModalProps) => {
    const { t } = useLanguage();
    const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
    const { classes } = useStyles(destructive)();

    const { action: actionConfirm, ...confirmProps } = confirm;
    const { action: actionCancel, ...cancelProps } = cancel;

    const topSection = (
      <div className={classes.topSection}>
        <p>{title}</p>
        {!hideCloseIcon && (
          <div className={classes.cancel}>
            <CloseIcon onClick={closeAction || cancel.action} />
          </div>
        )}
      </div>
    );

    const footerSection = (
      <div
        className={cn(classes?.buttonWrapper, {
          [classes.buttonsVerticalPosition]: type === 'vertical' && isMobile,
        })}
      >
        {!cancel.hidden && (
          <Button
            color="primary"
            size="large"
            classes={{ root: classes.button }}
            variant="outlined"
            {...cancelProps}
            onClick={actionCancel}
            data-testid="confirmation-modal-back"
          >
            {cancel.title || t('app.confirmationModal.back')}
          </Button>
        )}
        {!confirm.hidden && (
          <Button
            variant="contained"
            color="error"
            classes={{ root: classes.button }}
            {...confirmProps}
            size="large"
            onClick={actionConfirm}
            data-testid="confirmation-modal-confirm"
          >
            {confirm.title || t('app.confirm')}
          </Button>
        )}
      </div>
    );

    return (
      <ModalComponent onClose={cancel.action || (() => {})} open={open}>
        {topSection}
        {rawContent ? children : <div className={classes.content}>{children}</div>}
        {footerSection}
      </ModalComponent>
    );
  },
);
